import { useState, useRef, useEffect } from "react";
import { 
  Shield, Share2, Download, Check, Calendar, 
  Play, Trophy 
} from "lucide-react";

export const PlayoffsSection = ({ playoffs, teams, leagueColor }) => {
  const activePlayoffs = playoffs?.filter(p => p.is_active) || [];
  const [selectedPlayoff, setSelectedPlayoff] = useState(activePlayoffs?.[0]?.id || null);
  const [linkCopied, setLinkCopied] = useState(false);
  const bracketRef = useRef(null);

  // Update selected playoff when playoffs change
  useEffect(() => {
    if (activePlayoffs.length > 0 && !activePlayoffs.find(p => p.id === selectedPlayoff)) {
      setSelectedPlayoff(activePlayoffs[0].id);
    }
  }, [activePlayoffs, selectedPlayoff]);

  const getTeamById = (teamId) => {
    if (!teamId) return null;
    return teams?.find(t => t.id === teamId);
  };

  // Share bracket link
  const handleShareLink = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    });
  };

  // Export bracket as PDF
  const handleExportPDF = async () => {
    if (!bracketRef.current) return;
    
    try {
      const html2canvas = (await import('html2canvas')).default;
      const jsPDF = (await import('jspdf')).default;
      
      const canvas = await html2canvas(bracketRef.current, {
        backgroundColor: '#0a0a0a',
        scale: 2,
        useCORS: true
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: canvas.width > canvas.height ? 'landscape' : 'portrait',
        unit: 'px',
        format: [canvas.width, canvas.height]
      });
      
      pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
      pdf.save(`playoff-bracket.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('PDF export requires additional libraries. Please use the share link instead.');
    }
  };

  if (!activePlayoffs || activePlayoffs.length === 0) {
    return (
      <div className="bg-zinc-900/50 rounded-xl border border-zinc-800 p-12 text-center">
        <Shield className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
        <p className="text-zinc-500">No playoff brackets available</p>
      </div>
    );
  }

  const currentPlayoff = activePlayoffs.find(p => p.id === selectedPlayoff) || activePlayoffs[0];

  // Determine bracket type and team count
  const isDoubleSided = currentPlayoff?.bracket_type === "double_sided";
  const bracketSize = currentPlayoff?.bracket_size || 4;
  const totalTeams = isDoubleSided ? bracketSize * 2 : bracketSize;

  return (
    <div className="space-y-6" data-testid="playoffs-section">
      {/* Header with Playoff Selector and Share/Export */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        {/* Playoff Selector (if multiple) */}
        {activePlayoffs.length > 1 ? (
          <div className="flex flex-wrap gap-2">
            {activePlayoffs.map(playoff => (
              <button
                key={playoff.id}
                onClick={() => setSelectedPlayoff(playoff.id)}
                className={`px-4 py-2 text-sm font-bold uppercase transition-colors ${
                  selectedPlayoff === playoff.id 
                    ? 'text-black' 
                    : 'bg-zinc-800 text-zinc-400 hover:text-white'
                }`}
                style={{ backgroundColor: selectedPlayoff === playoff.id ? (leagueColor || '#f97316') : undefined }}
                data-testid={`playoff-selector-${playoff.id}`}
              >
                {playoff.name}
              </button>
            ))}
          </div>
        ) : (
          <h2 className="font-heading text-xl uppercase text-white tracking-wider flex items-center gap-3">
            <Shield className="w-6 h-6" style={{ color: leagueColor || '#f97316' }} />
            {currentPlayoff?.name || "Playoffs"}
          </h2>
        )}
        
        {/* Share & Export Buttons */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleShareLink}
            className="flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white text-sm transition-colors rounded"
            data-testid="share-bracket-btn"
          >
            {linkCopied ? (
              <>
                <Check className="w-4 h-4 text-green-500" />
                <span>Link Copied!</span>
              </>
            ) : (
              <>
                <Share2 className="w-4 h-4" />
                <span className="hidden sm:inline">Share</span>
              </>
            )}
          </button>
          <button
            onClick={handleExportPDF}
            className="flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white text-sm transition-colors rounded"
            data-testid="export-pdf-btn"
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Export PDF</span>
          </button>
        </div>
      </div>

      {/* Bracket Info */}
      <p className="text-sm text-zinc-500">
        {isDoubleSided ? "Conference Championship" : "Single Elimination"} 
        {" • "}{totalTeams} Teams
      </p>

      {/* Bracket Display */}
      <LeaguePlayoffBracket 
        playoff={currentPlayoff}
        teams={teams}
        leagueColor={leagueColor}
        bracketRef={bracketRef}
        getTeamById={getTeamById}
      />
    </div>
  );
};

// Main bracket renderer - ESPN-style
const LeaguePlayoffBracket = ({ playoff, teams, leagueColor, bracketRef, getTeamById }) => {
  if (!playoff) return null;

  const bracketSize = playoff.bracket_size || 4;
  const numRounds = Math.ceil(Math.log2(bracketSize));
  const isDoubleSided = playoff.bracket_type === "double_sided";
  const matchups = playoff.matchups || [];

  const getRoundTitle = (roundIdx, totalRounds, isConfFinal = false) => {
    if (isConfFinal) return "Conf Final";
    const roundsFromEnd = totalRounds - roundIdx;
    if (roundsFromEnd === 1) return "Championship";
    if (roundsFromEnd === 2) return "Semifinals";
    if (roundsFromEnd === 3 && totalRounds > 2) return "Quarterfinals";
    if (roundIdx === 0) return "Wild Card";
    return `Round ${roundIdx + 1}`;
  };

  // Double-sided (Conference) bracket
  if (isDoubleSided) {
    const sideAMatchups = matchups.filter(m => m.side === "A") || [];
    const sideBMatchups = matchups.filter(m => m.side === "B") || [];
    const championship = matchups.find(m => m.side === "championship");

    // Organize into rounds
    const sideARounds = [];
    const sideBRounds = [];
    for (let r = 1; r <= numRounds; r++) {
      sideARounds.push(sideAMatchups.filter(m => m.round === r));
      sideBRounds.push(sideBMatchups.filter(m => m.round === r));
    }

    return (
      <div className="overflow-x-auto pb-4" ref={bracketRef}>
        <div className="min-w-[900px] p-4 bg-zinc-950 rounded-lg">
          <div className="flex items-stretch">
            {/* Conference A Label */}
            <div className="flex items-center justify-center pr-4">
              <div 
                className="px-3 py-4 bg-blue-500/10 rounded-lg border border-blue-500/30 text-center"
                style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
              >
                <span className="text-blue-400 font-bold text-sm">
                  {playoff.conference_a_name || "Conference A"}
                </span>
              </div>
            </div>

            {/* Side A Rounds */}
            <div className="flex gap-4">
              {sideARounds.map((round, roundIdx) => (
                <BracketRound
                  key={`A-${roundIdx}`}
                  round={round}
                  roundIdx={roundIdx}
                  totalRounds={sideARounds.length}
                  title={getRoundTitle(roundIdx, sideARounds.length, roundIdx === sideARounds.length - 1)}
                  getTeamById={getTeamById}
                  leagueColor={leagueColor}
                  side="A"
                />
              ))}
            </div>

            {/* Championship Center */}
            <div className="flex flex-col items-center justify-center mx-6">
              <div className="text-center mb-4 px-4 py-2 bg-orange-500/10 rounded-lg border border-orange-500/30">
                <span className="text-orange-400 font-bold text-sm">CHAMPIONSHIP</span>
              </div>
              <div 
                className="flex items-center justify-center"
                style={{ minHeight: `${Math.pow(2, Math.max(numRounds - 2, 0)) * 92}px` }}
              >
                {championship ? (
                  <ESPNGameCard 
                    matchup={championship} 
                    getTeamById={getTeamById} 
                    leagueColor={leagueColor}
                    isChampionship
                  />
                ) : (
                  <div className="w-[200px] bg-zinc-900 rounded border border-dashed border-zinc-700 p-4 text-center">
                    <span className="text-xs text-zinc-600">Championship TBD</span>
                  </div>
                )}
              </div>
            </div>

            {/* Side B Rounds (reversed) */}
            <div className="flex gap-4 flex-row-reverse">
              {sideBRounds.map((round, roundIdx) => (
                <BracketRound
                  key={`B-${roundIdx}`}
                  round={round}
                  roundIdx={roundIdx}
                  totalRounds={sideBRounds.length}
                  title={getRoundTitle(roundIdx, sideBRounds.length, roundIdx === sideBRounds.length - 1)}
                  getTeamById={getTeamById}
                  leagueColor={leagueColor}
                  side="B"
                />
              ))}
            </div>

            {/* Conference B Label */}
            <div className="flex items-center justify-center pl-4">
              <div 
                className="px-3 py-4 bg-red-500/10 rounded-lg border border-red-500/30 text-center"
                style={{ writingMode: 'vertical-rl' }}
              >
                <span className="text-red-400 font-bold text-sm">
                  {playoff.conference_b_name || "Conference B"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Single-sided bracket
  const rounds = [];
  for (let r = 1; r <= numRounds; r++) {
    rounds.push(matchups.filter(m => m.round === r));
  }

  return (
    <div className="overflow-x-auto pb-4" ref={bracketRef}>
      <div className="min-w-[700px] p-4 bg-zinc-950 rounded-lg">
        <div className="flex items-stretch gap-6">
          {rounds.map((round, roundIdx) => (
            <BracketRound
              key={roundIdx}
              round={round}
              roundIdx={roundIdx}
              totalRounds={rounds.length}
              title={getRoundTitle(roundIdx, rounds.length)}
              getTeamById={getTeamById}
              leagueColor={leagueColor}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

// Bracket Round Column
const BracketRound = ({ round, roundIdx, totalRounds, title, getTeamById, leagueColor, side = null }) => {
  const matchupHeight = 76;
  const baseGap = 16;
  const verticalSpacing = Math.pow(2, roundIdx) * (matchupHeight + baseGap);
  const initialOffset = (verticalSpacing - matchupHeight) / 2;

  if (round.length === 0) {
    return (
      <div className="flex flex-col" style={{ minWidth: '200px' }}>
        <div className="text-center mb-4 px-2">
          <div className="text-xs font-bold uppercase text-zinc-500">{title}</div>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-xs text-zinc-600 italic">No matchups</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col" style={{ minWidth: '200px' }}>
      {/* Round Header */}
      <div className="text-center mb-4 px-2">
        <div className="text-xs font-bold uppercase text-zinc-400">{title}</div>
      </div>
      
      {/* Games with ESPN-style spacing */}
      <div 
        className="flex flex-col justify-around flex-1" 
        style={{ 
          gap: `${Math.max(verticalSpacing - matchupHeight, 8)}px`, 
          paddingTop: `${Math.max(initialOffset, 0)}px` 
        }}
      >
        {round.map((matchup) => (
          <ESPNGameCard 
            key={matchup.id} 
            matchup={matchup} 
            getTeamById={getTeamById} 
            leagueColor={leagueColor}
            side={side}
          />
        ))}
      </div>
    </div>
  );
};

// ESPN-Style Team Row Component
const ESPNTeamRow = ({ team, seed, score, isWinner, isChampionship, side }) => {
  return (
    <div className={`flex items-center gap-2 px-3 py-2.5 ${isWinner ? 'bg-green-500/10' : 'bg-zinc-900/80'}`}>
      {team ? (
        <>
          {/* Conference Indicator for Championship */}
          {isChampionship && side && (
            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
              side === 'A' ? 'bg-blue-500' : 'bg-red-500'
            }`}></span>
          )}
          {/* Seed Badge */}
          {seed && (
            <span className="text-[10px] text-yellow-500 font-bold w-4 text-center flex-shrink-0">{seed}</span>
          )}
          {team.logo_url ? (
            <img src={team.logo_url} alt={team.name} className="w-5 h-5 object-contain" />
          ) : (
            <div 
              className="w-5 h-5 rounded-sm flex items-center justify-center text-[9px] font-bold"
              style={{ backgroundColor: (team.color || '#666') + '30', color: team.color || '#666' }}
            >
              {team.name?.charAt(0)}
            </div>
          )}
          <span className={`text-xs flex-1 truncate ${isWinner ? 'text-white font-semibold' : 'text-zinc-300'}`}>
            {team.abbreviation || team.name}
          </span>
          {score !== null && score !== undefined && (
            <span className={`text-sm font-bold min-w-[24px] text-right ${isWinner ? 'text-green-400' : 'text-zinc-400'}`}>
              {score}
            </span>
          )}
        </>
      ) : (
        <span className="text-xs text-zinc-600 italic">TBD</span>
      )}
    </div>
  );
};

// ESPN-Style Game Card Component - Matches EventPage bracket style
const ESPNGameCard = ({ matchup, getTeamById, leagueColor, side = null, isChampionship = false }) => {
  const team1 = getTeamById(matchup.team1_id);
  const team2 = getTeamById(matchup.team2_id);

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr + "T12:00:00");
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Determine conference for championship game teams
  const team1Side = isChampionship ? "A" : null;
  const team2Side = isChampionship ? "B" : null;

  // Check if bye game
  if (matchup.is_bye && matchup.bye_team_id) {
    const byeTeam = getTeamById(matchup.bye_team_id);
    return (
      <div className="w-[200px] bg-zinc-900 rounded border border-dashed border-zinc-700 overflow-hidden">
        <div className="flex items-center gap-2 px-3 py-2.5 bg-zinc-800/50">
          {byeTeam?.logo_url ? (
            <img src={byeTeam.logo_url} alt={byeTeam.name} className="w-5 h-5 object-contain" />
          ) : (
            <div 
              className="w-5 h-5 rounded-sm flex items-center justify-center text-[9px] font-bold"
              style={{ backgroundColor: (byeTeam?.color || '#666') + '30', color: byeTeam?.color || '#666' }}
            >
              {byeTeam?.name?.charAt(0) || '?'}
            </div>
          )}
          <span className="text-xs text-zinc-400">
            {byeTeam?.abbreviation || byeTeam?.name || 'TBD'} - BYE
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Game Card - ESPN-style clean design */}
      <div className={`w-[200px] bg-zinc-900 rounded overflow-hidden shadow-lg ${
        isChampionship ? 'ring-2 ring-orange-500' : ''
      }`}>
        <ESPNTeamRow 
          team={team1} 
          seed={matchup.team1_seed}
          score={matchup.team1_score} 
          isWinner={matchup.winner_id && matchup.winner_id === matchup.team1_id} 
          isChampionship={isChampionship}
          side={team1Side}
        />
        <div className="h-px bg-zinc-800"></div>
        <ESPNTeamRow 
          team={team2} 
          seed={matchup.team2_seed}
          score={matchup.team2_score} 
          isWinner={matchup.winner_id && matchup.winner_id === matchup.team2_id} 
          isChampionship={isChampionship}
          side={team2Side}
        />
        
        {/* Game Info Footer */}
        {(matchup.game_date || matchup.game_time || matchup.youtube_url) && (
          <div className="px-3 py-1.5 bg-zinc-800/50 flex items-center justify-between">
            <div className="text-[10px] text-zinc-500 flex items-center gap-1">
              {matchup.game_date && (
                <>
                  <Calendar className="w-3 h-3" />
                  <span>{formatDate(matchup.game_date)}</span>
                </>
              )}
              {matchup.game_time && <span className="ml-1">{formatTime(matchup.game_time)}</span>}
            </div>
            {matchup.youtube_url && (
              <a 
                href={matchup.youtube_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[10px] px-2 py-0.5 bg-white/10 hover:bg-white/20 text-white rounded flex items-center gap-1"
              >
                <Play className="w-2.5 h-2.5" />
                Watch
              </a>
            )}
          </div>
        )}

        {/* TV Network badge */}
        {matchup.tv_network && (
          <div className="px-3 py-1 bg-zinc-800 text-[10px] text-zinc-400 text-center">
            {matchup.tv_network}
          </div>
        )}

        {/* Champion indicator for championship game */}
        {isChampionship && matchup.winner_id && (
          <div className="p-2 border-t border-orange-500/50 bg-orange-500/10 text-center">
            <div className="flex items-center justify-center gap-1 text-orange-400 font-bold text-sm">
              <Trophy className="w-4 h-4" />
              <span>CHAMPION</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
